import type { ReactNode } from 'react';
import type { MouseEvent as ReactMouseEvent } from 'react';
import { cx } from '../../utils/cx.js';
import { DataTableEmptyState } from './DataTableEmptyState.js';
import { DataTableRowActionsContent } from './DataTableRowActions.js';
import { DataTableRowSelectControl } from './DataTableRowSelectControl.js';
import type { Column, DataTableSelection } from './types.js';
import styles from './DataTable.module.css';

export function DataTableStackedView<T>({
  columns,
  rowSource,
  dataLength,
  emptyMessage,
  emptyDescription,
  emptyAction,
  emptyVariant = 'default',
  rowKey,
  hasActionColumn,
  selection,
  selectionActive = false,
  onToggleRowSelection,
  rowLeadingActions,
  rowActions,
  onRowClick,
  rowProps,
}: {
  columns: Column<T>[];
  rowSource: T[];
  dataLength: number;
  emptyMessage: string;
  emptyDescription?: ReactNode;
  emptyAction?: ReactNode;
  emptyVariant?: 'default' | 'filter' | 'resource';
  rowKey: (row: T) => string;
  hasActionColumn: boolean;
  selection?: DataTableSelection<T>;
  selectionActive?: boolean;
  onToggleRowSelection?: (key: string) => void;
  rowLeadingActions?: (row: T) => ReactNode;
  rowActions?: (row: T) => ReactNode;
  onRowClick?: (row: T) => void;
  rowProps?: (row: T) => import('react').HTMLAttributes<HTMLTableRowElement> | undefined;
}) {
  return (
    <div
      className={styles.stackedList}
      {...(selection && selectionActive ? { 'data-wp-datatable-selection-active': '' } : {})}
    >
      {dataLength === 0 ? (
        <DataTableEmptyState
          emptyMessage={emptyMessage}
          emptyDescription={emptyDescription}
          emptyAction={emptyAction}
          emptyVariant={emptyVariant}
          overlay={false}
        />
      ) : (
        rowSource.map((row) => {
          const extra = rowProps?.(row);
          const { className: extraRowClass, onClick: extraRowOnClick, ...extraRowRest } = extra ?? {};
          const mergedStackedOnClick =
            onRowClick || extraRowOnClick
              ? (e: ReactMouseEvent<HTMLDivElement>) => {
                  extraRowOnClick?.(e as ReactMouseEvent<HTMLTableRowElement>);
                  if (!onRowClick || e.defaultPrevented) return;
                  const rowEl = e.currentTarget;
                  const t = e.target;
                  if (!t || !(t instanceof Element)) return;
                  for (const root of rowEl.querySelectorAll('[data-datatable-rowclick-ignore]')) {
                    if (root.contains(t) || (t !== rowEl && t.contains(root))) return;
                  }
                  onRowClick(row);
                }
              : undefined;
          return (
            <div
              key={rowKey(row)}
              data-wp-datatable-card
              {...extraRowRest}
              className={cx(styles.stackedCard, onRowClick && styles.stackedCardClickable, extraRowClass)}
              onClick={mergedStackedOnClick}
            >
              {selection ? (
                <DataTableRowSelectControl
                  checked={selection.selectedKeys.has(rowKey(row))}
                  disabled={selection.isRowSelectable?.(row) === false}
                  ariaLabel={`Sélectionner la ligne ${rowKey(row)}`}
                  onChange={() => onToggleRowSelection?.(rowKey(row))}
                />
              ) : null}
              {columns.map((c) => (
                <div key={c.key} className={styles.stackedField}>
                  <span className={styles.stackedLabel}>
                    {typeof c.label === 'string' && c.label.trim() ? c.label : (c.headerAriaLabel ?? c.key)}
                  </span>
                  <div className={styles.stackedValue}>
                    {c.render ? c.render(row) : String((row as Record<string, unknown>)[c.key] ?? '')}
                  </div>
                </div>
              ))}
              {hasActionColumn ? (
                <DataTableRowActionsContent
                  stacked
                  leading={rowLeadingActions?.(row)}
                  menu={rowActions?.(row)}
                />
              ) : null}
            </div>
          );
        })
      )}
    </div>
  );
}
